import { useField } from 'informed';
import { taggedTemplateLiteral as _taggedTemplateLiteral, objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, extends as _extends } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import styled from '../../theming/styled-components.js';
import themes from '../../theming/themes.js';
import React, { useCallback, useMemo, useState, useEffect } from 'react';
import useOutsideClick from '../../hooks/useOutsideClick.js';
import { Icon, InputMessage } from '../../index.js';

var _excluded = ["onChange", "onBlur", "onFocus", "forwardedRef", "theme", "inputFocus", "setInputFocus", "field", "getTagApi", "onKeyDownHandler", "searchResultsRef", "hideResults"];
var _templateObject, _templateObject2, _templateObject3, _templateObject4;
var SearchInputContainer = styled('div')(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  ", "\n"])), function (_ref) {
  var inputFocus = _ref.inputFocus,
    _ref$theme = _ref.theme,
    theme = _ref$theme === void 0 ? themes.secureSite : _ref$theme;
  return "\n  min-height: 2.375rem;\n  position: relative;\n  color: ".concat(theme.palette.black, ";\n  border: 2px solid ").concat(theme.palette.grayLight, "\n  border-radius: 2px;\n  background-color: ").concat(theme.palette.white, ";\n  display: flex;\n  flex-wrap: wrap;\n\n  ").concat(inputFocus ? "border-color: ".concat(theme.palette.secondaryLighter) : '', ";\n");
});
var StyledInput = styled.input(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  border: none;\n  height: 2.375rem;\n  padding: 12px;\n  padding-left: 0.6875rem;\n  padding-right: 0.875rem;\n  font-size: 1rem;\n  font-weight: 400;\n  color: ", ";\n  width: 5rem;\n  flex-grow: 1;\n\n  &:focus,\n  &:active {\n    border: none;\n    box-shadow: none;\n    outline: none;\n  }\n"])), function (_ref2) {
  var _ref2$theme = _ref2.theme,
    theme = _ref2$theme === void 0 ? themes.secureSite : _ref2$theme;
  return theme.palette.black;
});
var ErrorTextDiv = styled.div(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n  margin-top: 5px;\n  padding-left: 0.25rem;\n  font-size: 0.875rem;\n  color: ", ";\n"])), function (_ref3) {
  var _ref3$theme = _ref3.theme,
    theme = _ref3$theme === void 0 ? themes.secureSite : _ref3$theme;
  return theme.palette.error;
});
var Tag = styled.div(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n  color: ", "\n  font-size: 0.875rem;\n  font-weight: 500;\n  max-width: 10rem;\n  margin: 0.25rem;\n  height: 1.875rem;\n  padding: 0.25rem 0 0.25rem 0.5rem;\n  border-radius: 15px;\n  background-color: ", ";\n  display:flex;\n\n  span {\n    overflow: hidden;\n    text-overflow: ellipsis;\n    white-space: nowrap;\n    margin: auto\n  }\n\n  button {\n    background-color: transparent;\n    color: inherit;\n    border: none;\n    margin: 0 0.25rem;\n    padding: 0 0.25rem;\n    display: flex;\n    align-items: center;\n\n    &:focus {\n      outline-color: ", ";\n    }\n\n    &:hover {\n      cursor: pointer;\n    }\n\n    i {\n      transform: scale(0.75);\n    }\n  }\n"])), function (_ref4) {
  var _ref4$theme = _ref4.theme,
    theme = _ref4$theme === void 0 ? themes.secureSite : _ref4$theme;
  return theme.palette.white;
}, function (_ref5) {
  var _ref5$theme = _ref5.theme,
    theme = _ref5$theme === void 0 ? themes.secureSite : _ref5$theme;
  return theme.palette.primary;
}, function (_ref6) {
  var _ref6$theme = _ref6.theme,
    theme = _ref6$theme === void 0 ? themes.secureSite : _ref6$theme;
  return theme.palette.secondaryLighter;
});
var TagInput = function TagInput(props) {
  var _useField = useField(props),
    fieldState = _useField.fieldState,
    fieldApi = _useField.fieldApi,
    render = _useField.render,
    userProps = _useField.userProps;
  var value = fieldState.value;
  var setValue = fieldApi.setValue,
    setTouched = fieldApi.setTouched;
  var onChange = userProps.onChange,
    onBlur = userProps.onBlur,
    onFocus = userProps.onFocus,
    forwardedRef = userProps.forwardedRef,
    _userProps$theme = userProps.theme,
    theme = _userProps$theme === void 0 ? themes.secureSite : _userProps$theme,
    inputFocus = userProps.inputFocus,
    setInputFocus = userProps.setInputFocus,
    field = userProps.field,
    getTagApi = userProps.getTagApi,
    onKeyDownHandler = userProps.onKeyDownHandler,
    searchResultsRef = userProps.searchResultsRef,
    hideResults = userProps.hideResults,
    rest = _objectWithoutProperties(userProps, _excluded);
  var handleBlur = useCallback(function (e) {
    setInputFocus(false);
    setTouched(true);
    if (onBlur) {
      onBlur(e);
    }
    hideResults();
  }, [setTouched, onBlur, setInputFocus]);
  var handleFocus = useCallback(function (e) {
    setInputFocus(true);
    if (onFocus) {
      onFocus(e);
    }
  }, [setInputFocus, onFocus]);
  var handleChange = useCallback(function (e) {
    setValue(e.target.value);
    if (onChange) {
      onChange(e);
    }
  }, [setValue, onChange]);
  var displayErrorMessage = fieldState.error && !inputFocus;
  var errorMessageId = useMemo(function () {
    return "".concat(field, "-error-message");
  }, [field]);
  var _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    tags = _useState2[0],
    setTags = _useState2[1];
  var removeTag = useCallback(function (id) {
    setTags(tags.filter(function (tag) {
      return tag.id !== id;
    }));
  }, [tags]);
  var addTag = useCallback(function (tag) {
    if (!tags.some(function (t) {
      return t.id === tag.id;
    })) {
      setTags(tags.concat([tag]));
    }
    setInputFocus(false);
  }, [tags]);
  var getTags = useCallback(function () {
    return tags;
  }, [tags]);
  var removeAllTags = useCallback(function () {
    return setTags([]);
  }, []);
  useEffect(function () {
    if (getTagApi) {
      getTagApi({
        removeTag: removeTag,
        addTag: addTag,
        getTags: getTags,
        removeAllTags: removeAllTags
      });
    }
  }, [removeTag, addTag, getTags, removeAllTags]);
  var handleKeyDown = function handleKeyDown(e) {
    if (e.keyCode === 8 && !value) {
      setTags(tags.filter(function (tag, i) {
        if (i < tags.length - 1) {
          return tag;
        }
      }));
    }
    if (onKeyDownHandler) {
      onKeyDownHandler(e);
    }
  };
  useOutsideClick([forwardedRef, searchResultsRef], handleBlur);
  return render(/*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(SearchInputContainer, {
    theme: theme,
    inputFocus: inputFocus
  }, tags.map(function (tag) {
    return /*#__PURE__*/React.createElement(Tag, {
      "data-testid": "search-tag",
      title: tag.value,
      theme: theme,
      key: "search-tag-".concat(tag.id)
    }, /*#__PURE__*/React.createElement("span", null, tag.value), /*#__PURE__*/React.createElement("button", {
      onClick: function onClick() {
        return removeTag(tag.id);
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      color: "white",
      name: "close",
      size: "xs"
    })));
  }), /*#__PURE__*/React.createElement(StyledInput, _extends({}, rest, {
    ref: forwardedRef,
    value: value,
    onKeyDown: handleKeyDown,
    onChange: handleChange,
    onFocus: handleFocus,
    theme: theme,
    field: field,
    "aria-controls": errorMessageId,
    "aria-describedby": errorMessageId
  }))), /*#__PURE__*/React.createElement(InputMessage, {
    style: {
      marginTop: '5px'
    },
    "aria-live": 'assertive',
    error: !!fieldState.error,
    role: 'alert',
    theme: themes.secureSite,
    showIcon: false,
    hidden: !displayErrorMessage,
    id: errorMessageId
  }, fieldState.error)));
};

export default TagInput;
